import React, {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {
  getMyQuotationForRfq,
  getVendorLogisticsChangeContextForQuotation,
  getQuotationCurrencyContext,
  saveQuotationDraft,
  submitVendorLogisticsChangeRequest,
  submitQuotation,
} from '../core/_requests'
import {CurrencyRateSourceType, QuotationCurrencyContext} from '../core/_models'
import {computeConvertedAmount, formatExchangeRateDisplay} from '../core/currencyConversion'
import MoneyDisplay from './MoneyDisplay'

type VendorQuotationEditorCardProps = {
  rfq: any
  visible: boolean
  onChanged?: () => void | Promise<void>
}

type VendorQuotationLineForm = {
  requisitionLineId: number
  quantity: number
  unitPrice: string
  discountPercent: string
  gstPercent: string
  remarks: string
  itemLabel: string
}

type VendorQuotationFormState = {
  id?: number | null
  rfqId: number
  vendorId?: number | null
  quotationCurrencyCode: string
  exchangeRate: string
  exchangeRateSource: CurrencyRateSourceType | ''
  promisedDeliveryDate: string
  totalDiscount: string
  freightCost: string
  packagingCost: string
  truckCost: string
  bargeCost: string
  otherCost: string
  vatOnLogistics: string
  locationOfDelivery: string
  termsOfPayment: string
  terms: string
  bankDetails: string
  remittanceInstructions: string
  termsAccepted: boolean
  lines: VendorQuotationLineForm[]
}

const numeric = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const stringValue = (value: unknown) => String(value ?? '')

const buildItemLabel = (line: any, index: number) =>
  String(
    line?.manualItemName ||
      line?.itemName ||
      line?.part?.name ||
      line?.subcomponent?.name ||
      line?.description ||
      line?.notes ||
      `Line ${index + 1}`
  )

const extractRfqLines = (rfq: any): VendorQuotationLineForm[] => {
  const lines = Array.isArray(rfq?.requisition?.lineItems)
    ? rfq.requisition.lineItems
    : Array.isArray(rfq?.requisition?.lines)
    ? rfq.requisition.lines
    : []
  return lines.map((line: any, index: number) => ({
    requisitionLineId: Number(line?.id || line?.requisitionLineId || index + 1),
    quantity: Math.max(0, numeric(line?.quantity)),
    unitPrice: '',
    discountPercent: '',
    gstPercent: '',
    remarks: '',
    itemLabel: buildItemLabel(line, index),
  }))
}

const buildInitialForm = (
  rfq: any,
  context: QuotationCurrencyContext,
  quotation: any | null
): VendorQuotationFormState => {
  const existingLines = Array.isArray(quotation?.lines) ? quotation.lines : []
  const fallbackLines = extractRfqLines(rfq)
  const mappedLines =
    existingLines.length > 0
      ? existingLines.map((line: any, index: number) => ({
          requisitionLineId: Number(line?.requisitionLineId || fallbackLines[index]?.requisitionLineId || index + 1),
          quantity: Math.max(0, numeric(line?.quantity || fallbackLines[index]?.quantity)),
          unitPrice: stringValue(line?.unitPrice ?? ''),
          discountPercent: stringValue(line?.discountPercent ?? ''),
          gstPercent: stringValue(line?.gstPercent ?? ''),
          remarks: stringValue(line?.remarks ?? ''),
          itemLabel: buildItemLabel(line, index),
        }))
      : fallbackLines
  return {
    id: quotation?.id ?? null,
    rfqId: Number(rfq?.id || 0),
    vendorId: quotation?.vendorId ?? null,
    quotationCurrencyCode:
      String(
        quotation?.quotationCurrencyCode ||
          context.quotationCurrencyCode ||
          context.vendorPreferredCurrencyCode ||
          context.buyerCompanyCurrencyCode ||
          'INR'
      ).toUpperCase(),
    exchangeRate:
      quotation?.exchangeRate != null
        ? String(quotation.exchangeRate)
        : context.exchangeRate != null
        ? String(context.exchangeRate)
        : '',
    exchangeRateSource:
      (quotation?.exchangeRateSource ||
        context.exchangeRateSource ||
        (context.sameCurrency ? 'IDENTITY' : '')) as CurrencyRateSourceType | '',
    promisedDeliveryDate: stringValue(quotation?.promisedDeliveryDate || ''),
    totalDiscount: stringValue(quotation?.totalDiscount ?? ''),
    freightCost: stringValue(quotation?.freightCost ?? ''),
    packagingCost: stringValue(quotation?.packagingCost ?? ''),
    truckCost: stringValue(quotation?.truckCost ?? ''),
    bargeCost: stringValue(quotation?.bargeCost ?? ''),
    otherCost: stringValue(quotation?.otherCost ?? ''),
    vatOnLogistics: stringValue(quotation?.vatOnLogistics ?? ''),
    locationOfDelivery: stringValue(quotation?.locationOfDelivery ?? ''),
    termsOfPayment: stringValue(quotation?.termsOfPayment ?? ''),
    terms: stringValue(quotation?.terms ?? ''),
    bankDetails: stringValue(quotation?.bankDetails ?? ''),
    remittanceInstructions: stringValue(quotation?.remittanceInstructions ?? ''),
    termsAccepted: Boolean(quotation?.termsAccepted),
    lines: mappedLines,
  }
}

const VendorQuotationEditorCard: FC<VendorQuotationEditorCardProps> = ({rfq, visible, onChanged}) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [available, setAvailable] = useState(false)
  const [context, setContext] = useState<QuotationCurrencyContext | null>(null)
  const [form, setForm] = useState<VendorQuotationFormState | null>(null)
  const [reworkRemark, setReworkRemark] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [termsModalOpen, setTermsModalOpen] = useState(false)
  const [logisticsModalOpen, setLogisticsModalOpen] = useState(false)
  const [logisticsRemark, setLogisticsRemark] = useState('')
  const [logisticsContext, setLogisticsContext] = useState<{canSubmitLogisticsChangeRequest: boolean; purchaseOrderId?: number | null; logisticsChangeRequestLockReason?: string | null} | null>(null)

  useEffect(() => {
    if (!visible || !rfq?.id) return
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const [currencyContext, quotation] = await Promise.all([
          getQuotationCurrencyContext(Number(rfq.id)),
          getMyQuotationForRfq(Number(rfq.id)),
        ])
        if (cancelled) return
        setAvailable(true)
        setContext(currencyContext)
        setForm(buildInitialForm(rfq, currencyContext, quotation))
        setReworkRemark(quotation?.reworkRemark || null)
      } catch (err: any) {
        if (cancelled) return
        const status = err?.status
        if (status === 403 || status === 404) {
          setAvailable(false)
          return
        }
        setError(err?.message || 'Failed to load vendor quotation form')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [visible, rfq])

  const buyerCurrency = String(context?.buyerCompanyCurrencyCode || 'INR').toUpperCase()
  const quotationCurrency = String(form?.quotationCurrencyCode || context?.quotationCurrencyCode || buyerCurrency).toUpperCase()
  const currentStatus = String(context?.status || '').toUpperCase()
  const editableStatuses = new Set(['', 'DRAFT', 'SENT_FOR_REWORK', 'REWORK_REQUESTED', 'OWNER_REWORK'])
  const isSentForRework = currentStatus === 'SENT_FOR_REWORK'
  const isEditable = editableStatuses.has(currentStatus)
  const sameCurrency = quotationCurrency === buyerCurrency
  const effectiveRate = sameCurrency ? 1 : Number(form?.exchangeRate)
  const exchangeRateDisplay =
    sameCurrency
      ? `1 ${quotationCurrency} = 1 ${buyerCurrency}`
      : formatExchangeRateDisplay(quotationCurrency, effectiveRate, buyerCurrency)

  useEffect(() => {
    if (!form) return
    if (sameCurrency && (form.exchangeRate !== '1' || form.exchangeRateSource !== 'IDENTITY')) {
      setForm((prev) =>
        prev
          ? {
              ...prev,
              exchangeRate: '1',
              exchangeRateSource: 'IDENTITY',
            }
          : prev
      )
      return
    }
    if (!sameCurrency && form.exchangeRateSource === 'IDENTITY') {
      setForm((prev) =>
        prev
          ? {
              ...prev,
              exchangeRateSource:
                (context?.suggestedRateSource as CurrencyRateSourceType | null) || 'VENDOR_PROVIDED',
            }
          : prev
      )
    }
  }, [context?.suggestedRateSource, form, sameCurrency])

  const preview = useMemo(() => {
    if (!form) return null
    const subtotal = form.lines.reduce((sum, line) => sum + numeric(line.quantity) * numeric(line.unitPrice), 0)
    const lineDiscount = form.lines.reduce(
      (sum, line) => sum + numeric(line.quantity) * numeric(line.unitPrice) * (numeric(line.discountPercent) / 100),
      0
    )
    const taxable = subtotal - lineDiscount
    const tax = form.lines.reduce((sum, line) => {
      const lineBase = numeric(line.quantity) * numeric(line.unitPrice)
      const lineNet = lineBase - lineBase * (numeric(line.discountPercent) / 100)
      return sum + lineNet * (numeric(line.gstPercent) / 100)
    }, 0)
    const logistics =
      numeric(form.freightCost) +
      numeric(form.packagingCost) +
      numeric(form.truckCost) +
      numeric(form.bargeCost) +
      numeric(form.otherCost) +
      numeric(form.vatOnLogistics)
    const headerDiscount = numeric(form.totalDiscount)
    const total = Math.max(0, taxable + tax + logistics - headerDiscount)
    const convert = (amount: number) =>
      computeConvertedAmount(amount, effectiveRate, quotationCurrency, buyerCurrency)
    return {
      subtotal,
      discount: lineDiscount + headerDiscount,
      tax,
      logistics,
      total,
      companySubtotal: convert(subtotal),
      companyDiscount: convert(lineDiscount + headerDiscount),
      companyTax: convert(tax),
      companyLogistics: convert(logistics),
      companyTotal: convert(total),
    }
  }, [buyerCurrency, effectiveRate, form, quotationCurrency])

  const updateLine = (index: number, key: keyof VendorQuotationLineForm, value: string) => {
    setForm((prev) =>
      prev
        ? {
            ...prev,
            lines: prev.lines.map((line, current) => (current === index ? {...line, [key]: value} : line)),
          }
        : prev
    )
  }

  const updateForm = (patch: Partial<VendorQuotationFormState>) =>
    setForm((prev) => (prev ? {...prev, ...patch} : prev))

  const validate = (submitMode: boolean) => {
    if (!form) return 'Quotation form is not ready.'
    if (!form.quotationCurrencyCode) return 'Quotation currency is required.'
    if (!sameCurrency) {
      const rate = Number(form.exchangeRate)
      if (!Number.isFinite(rate) || rate <= 0) {
        return 'Positive exchange rate is required when quotation currency differs from buyer company currency.'
      }
    }
    if (submitMode && !form.termsAccepted) {
      return 'Terms acceptance is required before submission.'
    }
    return null
  }

  const buildPayload = () => {
    if (!form) return null
    return {
      id: form.id,
      rfqId: form.rfqId,
      vendorId: form.vendorId,
      quotationCurrencyCode: form.quotationCurrencyCode,
      exchangeRate: sameCurrency ? 1 : Number(form.exchangeRate),
      exchangeRateSource: sameCurrency ? 'IDENTITY' : form.exchangeRateSource || 'VENDOR_PROVIDED',
      promisedDeliveryDate: form.promisedDeliveryDate || null,
      totalDiscount: numeric(form.totalDiscount),
      freightCost: numeric(form.freightCost),
      packagingCost: numeric(form.packagingCost),
      truckCost: numeric(form.truckCost),
      bargeCost: numeric(form.bargeCost),
      otherCost: numeric(form.otherCost),
      vatOnLogistics: numeric(form.vatOnLogistics),
      locationOfDelivery: form.locationOfDelivery || null,
      termsOfPayment: form.termsOfPayment || null,
      terms: form.terms || null,
      bankDetails: form.bankDetails || null,
      remittanceInstructions: form.remittanceInstructions || null,
      termsAccepted: form.termsAccepted,
      lines: form.lines.map((line) => ({
        requisitionLineId: line.requisitionLineId,
        quantity: numeric(line.quantity),
        assignedQuantity: numeric(line.quantity),
        unitPrice: numeric(line.unitPrice),
        discountPercent: numeric(line.discountPercent),
        gstPercent: numeric(line.gstPercent),
        remarks: line.remarks || null,
      })),
    }
  }

  const refresh = async () => {
    if (onChanged) {
      await onChanged()
    }
    const currencyContext = await getQuotationCurrencyContext(Number(rfq.id))
    const quotation = await getMyQuotationForRfq(Number(rfq.id))
    setContext(currencyContext)
    setForm(buildInitialForm(rfq, currencyContext, quotation))
    setReworkRemark(quotation?.reworkRemark || null)
  }

  const handleSave = async (submitMode: boolean) => {
    const validation = validate(submitMode)
    if (validation) {
      toast.error(validation)
      return
    }
    const payload = buildPayload()
    if (!payload) return
    try {
      setSaving(true)
      if (submitMode) {
        await submitQuotation(payload)
        toast.success('Quotation submitted')
      } else {
        await saveQuotationDraft(payload)
        toast.success('Quotation draft saved')
      }
      await refresh()
    } catch (err: any) {
      toast.error(err?.message || `Failed to ${submitMode ? 'submit' : 'save'} quotation`)
    } finally {
      setSaving(false)
    }
  }

  const openLogisticsUpdate = async () => {
    if (!form?.id) return
    try {
      const next = await getVendorLogisticsChangeContextForQuotation(form.id)
      setLogisticsContext(next)
      setLogisticsModalOpen(true)
    } catch (err: any) {
      toast.error(err?.message || 'Unable to load logistics update options')
    }
  }

  const submitLogisticsUpdate = async () => {
    if (!form?.id || !logisticsContext?.canSubmitLogisticsChangeRequest) return
    try {
      setSaving(true)
      await submitVendorLogisticsChangeRequest({
        quotationId: form.id,
        purchaseOrderId: logisticsContext.purchaseOrderId ?? null,
        freightCost: numeric(form.freightCost),
        packagingCost: numeric(form.packagingCost),
        truckCost: numeric(form.truckCost),
        bargeCost: numeric(form.bargeCost),
        otherCost: numeric(form.otherCost),
        vatOnLogistics: numeric(form.vatOnLogistics),
        totalDiscount: numeric(form.totalDiscount),
        vendorRemarks: logisticsRemark.trim() || undefined,
      })
      setLogisticsModalOpen(false)
      setLogisticsRemark('')
      toast.success('Logistics update submitted for company review')
      await refresh()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to submit logistics update')
    } finally {
      setSaving(false)
    }
  }

  if (!visible || !rfq?.id || !available) {
    return null
  }

  return (
    <div className='card border border-primary-subtle shadow-sm mt-6'>
      <div className='card-header border-0 pt-5'>
        <div>
          <h4 className='card-title align-items-start flex-column mb-1'>
            <span className='card-label fw-bold text-dark'>Vendor Quotation</span>
          </h4>
          <span className='text-muted fs-7'>
            Buyer currency: {buyerCurrency}
            {context?.status ? ` • Current status: ${String(context.status).replace(/_/g, ' ')}` : ''}
          </span>
          {isSentForRework ? (
            <div className='mt-3'>
              <span className='badge bg-warning text-dark px-3 py-2'>Sent for Rework</span>
            </div>
          ) : null}
        </div>
      </div>
      <div className='card-body pt-3'>
        {loading ? (
          <div className='text-muted'>Loading quotation form...</div>
        ) : error ? (
          <div className='alert alert-danger py-3 mb-0'>{error}</div>
        ) : form && preview ? (
          <>
            {isSentForRework ? (
              <div className='alert alert-warning text-dark py-3 mb-4'>
                <div className='fw-bold mb-1'>This quotation has been sent back for rework.</div>
                <div>Please review the buyer&apos;s revision remarks, update the quotation, and resubmit.</div>
                {reworkRemark ? (
                  <div className='mt-2'>
                    <strong>Revision Requested</strong>
                    <div className='mt-1'>{reworkRemark}</div>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className='row g-4 mb-4'>
              <div className='col-md-3'>
                <label className='form-label fw-semibold'>Quotation Currency</label>
                <input
                  className='form-control'
                  value={form.quotationCurrencyCode}
                  onChange={(e) =>
                    updateForm({
                      quotationCurrencyCode: e.target.value.toUpperCase(),
                      exchangeRateSource: sameCurrency ? 'IDENTITY' : form.exchangeRateSource,
                    })
                  }
                  disabled={!isEditable}
                />
              </div>
              <div className='col-md-3'>
                <label className='form-label fw-semibold'>Buyer Company Currency</label>
                <input className='form-control' value={buyerCurrency} disabled />
              </div>
              <div className='col-md-3'>
                <label className='form-label fw-semibold'>Rate Source</label>
                <select
                  className='form-select'
                  value={sameCurrency ? 'IDENTITY' : form.exchangeRateSource}
                  onChange={(e) =>
                    updateForm({exchangeRateSource: e.target.value as CurrencyRateSourceType})
                  }
                  disabled={sameCurrency || !isEditable}
                >
                  {sameCurrency ? <option value='IDENTITY'>IDENTITY</option> : null}
                  {!sameCurrency ? <option value='VENDOR_PROVIDED'>VENDOR_PROVIDED</option> : null}
                  {!sameCurrency && context?.suggestedRateSource ? (
                    <option value={context.suggestedRateSource}>{context.suggestedRateSource}</option>
                  ) : null}
                </select>
              </div>
              <div className='col-md-3'>
                <label className='form-label fw-semibold'>Delivery Date</label>
                <input
                  type='date'
                  className='form-control'
                      value={form.promisedDeliveryDate}
                      onChange={(e) => updateForm({promisedDeliveryDate: e.target.value})}
                      disabled={!isEditable}
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold'>Exchange Rate</label>
                {sameCurrency ? (
                  <div className='form-control bg-light'>Same currency. Conversion rate = 1.</div>
                ) : (
                  <>
                    <input
                      type='number'
                      min='0.00000001'
                      step='any'
                      className='form-control'
                      value={form.exchangeRate}
                      onChange={(e) => updateForm({exchangeRate: e.target.value})}
                      placeholder={context?.rateDirectionLabel || `1 ${quotationCurrency} = rate ${buyerCurrency}`}
                      disabled={!isEditable}
                    />
                    <div className='text-muted fs-8 mt-1'>
                      Suggested: {context?.suggestedRateDisplay || 'No reference rate available'}
                    </div>
                  </>
                )}
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold'>Conversion Preview</label>
                <div className='form-control bg-light h-auto py-3'>
                  <div className='fw-semibold'>{exchangeRateDisplay}</div>
                  {!sameCurrency && context?.suggestedRateDisplay ? (
                    <div className='text-muted fs-8 mt-1'>Reference: {context.suggestedRateDisplay}</div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className='table-responsive mb-4'>
              <table className='table table-row-bordered align-middle'>
                <thead>
                  <tr className='fw-semibold text-muted'>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Unit Price</th>
                    <th>Discount %</th>
                    <th>Tax %</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((line, index) => (
                    <tr key={line.requisitionLineId || index}>
                      <td>{line.itemLabel}</td>
                      <td>{line.quantity}</td>
                      <td>
                        <input
                          className='form-control'
                          type='number'
                          min='0'
                          step='0.01'
                          value={line.unitPrice}
                          onChange={(e) => updateLine(index, 'unitPrice', e.target.value)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <input
                          className='form-control'
                          type='number'
                          min='0'
                          step='0.01'
                          value={line.discountPercent}
                          onChange={(e) => updateLine(index, 'discountPercent', e.target.value)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <input
                          className='form-control'
                          type='number'
                          min='0'
                          step='0.01'
                          value={line.gstPercent}
                          onChange={(e) => updateLine(index, 'gstPercent', e.target.value)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <input
                          className='form-control'
                          value={line.remarks}
                          onChange={(e) => updateLine(index, 'remarks', e.target.value)}
                          disabled={!isEditable}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='row g-4 mb-4'>
              <div className='col-md-6'>
                <label className='form-label fw-semibold d-block'>Terms &amp; Conditions</label>
                <button type='button' className='btn btn-link p-0' onClick={() => setTermsModalOpen(true)}>
                  View Terms &amp; Conditions
                </button>
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold'>Terms of Payment</label>
                <textarea
                  className='form-control'
                  rows={4}
                  value={form.termsOfPayment}
                  onChange={(e) => updateForm({termsOfPayment: e.target.value})}
                  disabled={!isEditable}
                />
              </div>
            </div>

            <div className='row g-4 mb-4'>
              <div className='col-md-2'>
                <label className='form-label fw-semibold'>Freight</label>
                <input className='form-control' value={form.freightCost} onChange={(e) => updateForm({freightCost: e.target.value})} disabled={!isEditable} />
              </div>
              <div className='col-md-2'>
                <label className='form-label fw-semibold'>Packaging</label>
                <input className='form-control' value={form.packagingCost} onChange={(e) => updateForm({packagingCost: e.target.value})} disabled={!isEditable} />
              </div>
              <div className='col-md-2'>
                <label className='form-label fw-semibold'>Truck</label>
                <input className='form-control' value={form.truckCost} onChange={(e) => updateForm({truckCost: e.target.value})} disabled={!isEditable} />
              </div>
              <div className='col-md-2'>
                <label className='form-label fw-semibold'>Barge</label>
                <input className='form-control' value={form.bargeCost} onChange={(e) => updateForm({bargeCost: e.target.value})} disabled={!isEditable} />
              </div>
              <div className='col-md-2'>
                <label className='form-label fw-semibold'>Other</label>
                <input className='form-control' value={form.otherCost} onChange={(e) => updateForm({otherCost: e.target.value})} disabled={!isEditable} />
              </div>
              <div className='col-md-2'>
                <label className='form-label fw-semibold'>VAT on Logistics</label>
                <input className='form-control' value={form.vatOnLogistics} onChange={(e) => updateForm({vatOnLogistics: e.target.value})} disabled={!isEditable} />
              </div>
            </div>

            <div className='row g-4 align-items-start'>
              <div className='col-lg-6'>
                <div className='border rounded p-4 bg-light-primary'>
                  <div className='fw-bold text-dark mb-3'>Vendor Amounts</div>
                  <div className='d-flex justify-content-between py-1'><span>Subtotal</span><MoneyDisplay compact primaryAmount={preview.subtotal} primaryCurrency={quotationCurrency} sameCurrency /></div>
                  <div className='d-flex justify-content-between py-1'><span>Discount</span><MoneyDisplay compact primaryAmount={preview.discount} primaryCurrency={quotationCurrency} sameCurrency /></div>
                  <div className='d-flex justify-content-between py-1'><span>Tax</span><MoneyDisplay compact primaryAmount={preview.tax} primaryCurrency={quotationCurrency} sameCurrency /></div>
                  <div className='d-flex justify-content-between py-1'><span>Logistics</span><MoneyDisplay compact primaryAmount={preview.logistics} primaryCurrency={quotationCurrency} sameCurrency /></div>
                  <div className='d-flex justify-content-between py-2 border-top mt-2'><span className='fw-bold'>Total</span><MoneyDisplay compact primaryAmount={preview.total} primaryCurrency={quotationCurrency} sameCurrency /></div>
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='border rounded p-4 bg-light-success'>
                  <div className='fw-bold text-dark mb-3'>Buyer Company Equivalent</div>
                  <div className='d-flex justify-content-between py-1'><span>Subtotal</span><MoneyDisplay compact primaryAmount={preview.companySubtotal} primaryCurrency={buyerCurrency} secondaryAmount={preview.subtotal} secondaryCurrency={quotationCurrency} exchangeRateDisplay={exchangeRateDisplay} sameCurrency={sameCurrency} /></div>
                  <div className='d-flex justify-content-between py-1'><span>Discount</span><MoneyDisplay compact primaryAmount={preview.companyDiscount} primaryCurrency={buyerCurrency} secondaryAmount={preview.discount} secondaryCurrency={quotationCurrency} exchangeRateDisplay={exchangeRateDisplay} sameCurrency={sameCurrency} /></div>
                  <div className='d-flex justify-content-between py-1'><span>Tax</span><MoneyDisplay compact primaryAmount={preview.companyTax} primaryCurrency={buyerCurrency} secondaryAmount={preview.tax} secondaryCurrency={quotationCurrency} exchangeRateDisplay={exchangeRateDisplay} sameCurrency={sameCurrency} /></div>
                  <div className='d-flex justify-content-between py-1'><span>Logistics</span><MoneyDisplay compact primaryAmount={preview.companyLogistics} primaryCurrency={buyerCurrency} secondaryAmount={preview.logistics} secondaryCurrency={quotationCurrency} exchangeRateDisplay={exchangeRateDisplay} sameCurrency={sameCurrency} /></div>
                  <div className='d-flex justify-content-between py-2 border-top mt-2'><span className='fw-bold'>Total</span><MoneyDisplay compact primaryAmount={preview.companyTotal} primaryCurrency={buyerCurrency} secondaryAmount={preview.total} secondaryCurrency={quotationCurrency} exchangeRateDisplay={exchangeRateDisplay} sameCurrency={sameCurrency} /></div>
                </div>
              </div>
            </div>

            <div className='form-check form-check-custom form-check-solid mt-5'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={form.termsAccepted}
                onChange={(e) => updateForm({termsAccepted: e.target.checked})}
                disabled={!isEditable}
                id={`quotation-terms-${rfq.id}`}
              />
              <label className='form-check-label ms-2' htmlFor={`quotation-terms-${rfq.id}`}>
                I confirm the quotation currency, rate, and submitted commercial terms.
              </label>
            </div>

            {!isEditable ? (
              <div className='alert alert-light-info mt-5 mb-0'>
                Submitted quotation is read-only. Edit is available only for drafts or quotations returned for rework.
              </div>
            ) : null}
            <div className='d-flex justify-content-end gap-3 mt-5'>
              {form.id ? (
                <button type='button' className='btn btn-light-primary' disabled={saving} onClick={() => void openLogisticsUpdate()}>
                  Update Vendor Logistics
                </button>
              ) : null}
              <button
                type='button'
                className='btn btn-light-primary'
                onClick={() => handleSave(false)}
                disabled={saving || !isEditable}
              >
                Save Draft
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={() => handleSave(true)}
                disabled={saving || !isEditable}
              >
                Submit Quotation
              </button>
            </div>
            {termsModalOpen ? (
              <div className='modal fade show d-flex align-items-center justify-content-center' style={{backgroundColor: 'rgba(0,0,0,0.45)', position: 'fixed', inset: 0, zIndex: 1060}}>
                <div className='modal-dialog modal-lg modal-dialog-centered'>
                  <div className='modal-content bg-white'>
                    <div className='modal-header'>
                      <h5 className='modal-title'>Terms &amp; Conditions</h5>
                      <button type='button' className='btn-close' onClick={() => setTermsModalOpen(false)} />
                    </div>
                    <div className='modal-body' style={{whiteSpace: 'pre-wrap', maxHeight: '60vh', overflowY: 'auto'}}>
                      {form.terms?.trim() || 'No terms and conditions were provided for this quotation.'}
                    </div>
                    <div className='modal-footer'>
                      <button type='button' className='btn btn-light' onClick={() => setTermsModalOpen(false)}>Close</button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {logisticsModalOpen ? (
              <div className='modal fade show d-flex align-items-center justify-content-center' style={{backgroundColor: 'rgba(0,0,0,0.45)', position: 'fixed', inset: 0, zIndex: 1060}}>
                <div className='modal-dialog modal-lg modal-dialog-centered'>
                  <div className='modal-content bg-white'>
                    <div className='modal-header'><h5 className='modal-title'>Update Vendor Logistics</h5><button type='button' className='btn-close' onClick={() => setLogisticsModalOpen(false)} /></div>
                    <div className='modal-body'>
                      {logisticsContext?.canSubmitLogisticsChangeRequest ? (
                        <>
                          <p className='text-muted'>The current logistics values will be proposed to the company for review. They do not change the approved PO until accepted.</p>
                          <label className='form-label fw-semibold'>Vendor Remarks</label>
                          <textarea className='form-control' rows={4} value={logisticsRemark} onChange={(event) => setLogisticsRemark(event.target.value)} />
                        </>
                      ) : <div className='alert alert-warning mb-0'>{logisticsContext?.logisticsChangeRequestLockReason || 'This logistics update is not currently available.'}</div>}
                    </div>
                    <div className='modal-footer'><button type='button' className='btn btn-light' onClick={() => setLogisticsModalOpen(false)}>Close</button>{logisticsContext?.canSubmitLogisticsChangeRequest ? <button type='button' className='btn btn-primary' disabled={saving} onClick={() => void submitLogisticsUpdate()}>Submit for Review</button> : null}</div>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  )
}

export default VendorQuotationEditorCard
